/**
 * useAiTeam — AI 员工团队数据 Hook
 *
 * 从 GET /api/v1/ai-employees                获取当前租户的 AI 员工列表
 * 从 GET /api/v1/ai-employees/team-overview   获取团队仪表盘汇总
 *
 * 支持按部门过滤: transportation / warehouse / freight / general
 */

import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';

// ─── Types ─────────────────────────────────────────

export interface AiEmployee {
  id: string;
  employeeCode: string;
  name: string;
  nameZh?: string;
  role: string;
  department: string;
  status: 'active' | 'idle' | 'processing' | 'paused' | 'error';
  avatarUrl?: string;
  executionMode: string;
  riskClass: string;
  skills?: Array<{ skillId: string; enabled: boolean }>;
  metrics?: {
    processed: number;
    queue: number;
    lastActiveAt?: string;
  };
  activity?: string;
}

export interface TeamOverview {
  totalAgents: number;
  activeCount: number;
  idleCount: number;
  processingCount: number;
  errorCount: number;
  agents: AiEmployee[];
}

interface UseAiTeamOptions {
  department?: 'transportation' | 'warehouse' | 'freight' | 'general';
  enabled?: boolean;
}

interface UseAiTeamResult {
  agents: AiEmployee[];
  overview: TeamOverview | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

// ─── Hook ──────────────────────────────────────────

export function useAiTeam(opts?: UseAiTeamOptions): UseAiTeamResult {
  const [agents, setAgents] = useState<AiEmployee[]>([]);
  const [overview, setOverview] = useState<TeamOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const enabled = opts?.enabled !== false;

  const fetchTeam = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (opts?.department) params.set('department', opts.department);
      const queryStr = params.toString();

      // Fetch employee list and team overview in parallel
      const [employeesRes, overviewRes] = await Promise.allSettled([
        api.get<{ items: AiEmployee[] }>(
          `/ai-employees${queryStr ? `?${queryStr}` : ''}`
        ),
        api.get<TeamOverview>('/ai-employees/team-overview'),
      ]);

      if (employeesRes.status === 'fulfilled') {
        let list = employeesRes.value.items ?? [];
        if (opts?.department) {
          list = list.filter((a) => a.department === opts.department);
        }
        setAgents(list);
      }

      if (overviewRes.status === 'fulfilled') {
        setOverview(overviewRes.value);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load AI team');
    } finally {
      setLoading(false);
    }
  }, [opts?.department, enabled]);

  useEffect(() => {
    void fetchTeam();
  }, [fetchTeam]);

  return { agents, overview, loading, error, refresh: fetchTeam };
}

// ─── Fallback data per domain (used when API not yet seeded) ───

export const TMS_AI_AGENTS_SPEC: AiEmployee[] = [
  { id: 'agent.trucking.jess', employeeCode: 'JESS', name: 'Jess', role: 'DO Parser & Job Creator', department: 'transportation', status: 'active', executionMode: 'AI_WITH_APPROVAL', riskClass: 'R1', metrics: { processed: 0, queue: 0 } },
  { id: 'agent.trucking.tracker', employeeCode: 'TRACKER', name: 'Tracker', role: 'PSA PortNet Monitor', department: 'transportation', status: 'active', executionMode: 'AI_AUTONOMOUS', riskClass: 'R0', metrics: { processed: 0, queue: 0 } },
  { id: 'agent.trucking.jerry', employeeCode: 'JERRY', name: 'Jerry', role: 'Dispatch Optimizer', department: 'transportation', status: 'active', executionMode: 'AI_WITH_APPROVAL', riskClass: 'R2', metrics: { processed: 0, queue: 0 } },
  { id: 'agent.trucking.helen', employeeCode: 'HELEN', name: 'Helen', role: 'Document Validator & Biller', department: 'transportation', status: 'active', executionMode: 'AI_WITH_APPROVAL', riskClass: 'R2', metrics: { processed: 0, queue: 0 } },
  { id: 'agent.trucking.grace', employeeCode: 'GRACE', name: 'Grace', role: 'Customer Success', department: 'transportation', status: 'active', executionMode: 'AI_WITH_APPROVAL', riskClass: 'R1', metrics: { processed: 0, queue: 0 } },
  { id: 'agent.trucking.watch', employeeCode: 'WATCH', name: 'Watch', role: 'Demurrage Risk Monitor', department: 'transportation', status: 'active', executionMode: 'AI_AUTONOMOUS', riskClass: 'R1', metrics: { processed: 0, queue: 0 } },
  { id: 'agent.trucking.bean', employeeCode: 'BEAN', name: 'Bean', role: 'Finance & Driver Pay', department: 'transportation', status: 'idle', executionMode: 'AI_WITH_APPROVAL', riskClass: 'R3', metrics: { processed: 0, queue: 0 } },
];

export const FMS_AI_AGENTS_SPEC: AiEmployee[] = [
  { id: 'agent.freight.rex', employeeCode: 'REX', name: 'REX', role: 'Rate & Quote', department: 'freight', status: 'active', executionMode: 'AI_WITH_APPROVAL', riskClass: 'R1', metrics: { processed: 0, queue: 0 } },
  { id: 'agent.freight.iris', employeeCode: 'IRIS', name: 'IRIS', role: 'Import Shipment', department: 'freight', status: 'active', executionMode: 'AI_WITH_APPROVAL', riskClass: 'R2', metrics: { processed: 0, queue: 0 } },
  { id: 'agent.freight.echo', employeeCode: 'ECHO', name: 'ECHO', role: 'Export Shipment', department: 'freight', status: 'active', executionMode: 'AI_WITH_APPROVAL', riskClass: 'R2', metrics: { processed: 0, queue: 0 } },
  { id: 'agent.freight.cleo', employeeCode: 'CLEO', name: 'CLEO', role: 'Consolidation & Coload', department: 'freight', status: 'active', executionMode: 'AI_WITH_APPROVAL', riskClass: 'R2', metrics: { processed: 0, queue: 0 } },
  { id: 'agent.freight.doc', employeeCode: 'DOC', name: 'DOC', role: 'Document Processor', department: 'freight', status: 'active', executionMode: 'AI_AUTONOMOUS', riskClass: 'R1', metrics: { processed: 0, queue: 0 } },
  { id: 'agent.freight.grace', employeeCode: 'GRACE', name: 'GRACE', role: 'Customer Comms', department: 'freight', status: 'active', executionMode: 'AI_WITH_APPROVAL', riskClass: 'R1', metrics: { processed: 0, queue: 0 } },
  { id: 'agent.freight.bean', employeeCode: 'BEAN', name: 'BEAN', role: 'Finance & Settlement', department: 'freight', status: 'idle', executionMode: 'AI_WITH_APPROVAL', riskClass: 'R3', metrics: { processed: 0, queue: 0 } },
];

export const WMS_AI_AGENTS_SPEC: AiEmployee[] = [
  { id: 'agent.inbound', employeeCode: 'INBOUND', name: 'Arrival Inspector', role: 'Inbound + Receiving', department: 'warehouse', status: 'active', executionMode: 'AI_WITH_APPROVAL', riskClass: 'R1', metrics: { processed: 0, queue: 0 } },
  { id: 'agent.inventory', employeeCode: 'INVENTORY', name: 'Stock Intelligence', role: 'Inventory Monitoring', department: 'warehouse', status: 'active', executionMode: 'AI_AUTONOMOUS', riskClass: 'R0', metrics: { processed: 0, queue: 0 } },
  { id: 'agent.outbound', employeeCode: 'OUTBOUND', name: 'Fulfillment Coordinator', role: 'Release + Pick + Load', department: 'warehouse', status: 'active', executionMode: 'AI_WITH_APPROVAL', riskClass: 'R2', metrics: { processed: 0, queue: 0 } },
  { id: 'agent.ops', employeeCode: 'OPS', name: 'Ops Scheduler', role: 'Work Orders & VAS', department: 'warehouse', status: 'active', executionMode: 'AI_WITH_APPROVAL', riskClass: 'R1', metrics: { processed: 0, queue: 0 } },
  { id: 'agent.billing', employeeCode: 'BILLING', name: 'Billing Accountant', role: 'Storage + Handling Fees', department: 'warehouse', status: 'idle', executionMode: 'AI_WITH_APPROVAL', riskClass: 'R3', metrics: { processed: 0, queue: 0 } },
  { id: 'agent.compliance', employeeCode: 'COMPLIANCE', name: 'Compliance Officer', role: 'Audit + DG + Permits', department: 'warehouse', status: 'active', executionMode: 'AI_WITH_APPROVAL', riskClass: 'R2', metrics: { processed: 0, queue: 0 } },
  { id: 'agent.customer', employeeCode: 'CUSTOMER', name: 'Customer Success', role: 'Inquiries + Status Updates', department: 'warehouse', status: 'active', executionMode: 'AI_WITH_APPROVAL', riskClass: 'R1', metrics: { processed: 0, queue: 0 } },
];
